import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { CornerUpLeft, Sparkles } from "lucide-react";
import {
  experiencesByTag,
  getExperience,
  getIndustry,
  getRole,
  getSkill,
  getTechnology,
  projectsByTag,
} from "../lib/queries";
import { TECH_CAT_HUE, lensHueFor, lensIconFor } from "../lib/visuals";
import { ShareThoughts } from "../components/ShareThoughts";
import { BrandLogo } from "../lib/brands";
import { openAsk } from "../components/ChatPanel";
import type { Experience, Project, Tag } from "../types";

type FocusLens = "industry" | "technology" | "skill" | "role";

const LENS_META: Record<
  FocusLens,
  { param: string; plural: string; get: (id: string) => Tag | undefined }
> = {
  industry: { param: "industry", plural: "industries", get: getIndustry },
  technology: { param: "tech", plural: "technologies", get: getTechnology },
  skill: { param: "skill", plural: "skills", get: getSkill },
  role: { param: "role", plural: "roles", get: getRole },
};

function isFocusLens(lens: string): lens is FocusLens {
  return lens in LENS_META;
}

function yearOf(d: string): number {
  if (!d || d === "present") return new Date().getFullYear();
  return parseInt(d.slice(0, 4), 10);
}

function yearSpan(start: string, end: string): string {
  const fmt = (d: string) => (d === "present" ? "" : d.slice(0, 4));
  const s = fmt(start);
  const e = fmt(end);
  return s === e ? s : `${s} – ${e}`;
}

function ExpCard({ e }: { e: Experience }) {
  const present = e.end === "present";
  return (
    <Link to={`/experience/${e.id}`} className="lens-exp card card-link card-pad">
      <BrandLogo company={e.company} size={44} />
      <div className="grow">
        <div className="arc-top">
          <div className="arc-co">
            {e.company}
            {present && <span className="now-tag mono">Now</span>}
          </div>
          <div className="arc-dates mono">
            {present ? `${yearOf(e.start)} – Present` : yearSpan(e.start, e.end)}
          </div>
        </div>
        <div className="arc-role">{e.role}</div>
        <p className="arc-summary">{e.summary}</p>
      </div>
    </Link>
  );
}

function ProjCard({ p }: { p: Project }) {
  const e = getExperience(p.experienceId);
  return (
    <Link to={`/project/${p.id}`} className="lens-proj card card-link card-pad">
      <div className="lens-proj-co mono">{e ? e.company : ""}</div>
      <div className="lens-proj-title">{p.title}</div>
      <p className="lens-proj-sum">{p.summary}</p>
      <div className="wrapflex" style={{ gap: 6, marginTop: "auto", paddingTop: 12 }}>
        {p.technologies.slice(0, 3).map((tid) => {
          const t = getTechnology(tid);
          const hue = TECH_CAT_HUE[t?.category ?? ""] ?? "var(--accent)";
          return (
            <span key={tid} className="mini-tag">
              <span className="dot" style={{ background: hue }} />
              {t?.label}
            </span>
          );
        })}
      </div>
    </Link>
  );
}

export function Lens() {
  const { lens } = useParams<{ lens: string }>();
  const [params] = useSearchParams();

  if (lens === "timeline") return <Navigate to="/?cview=timeline" replace />;
  if (!lens || !isFocusLens(lens)) return <Navigate to="/" replace />;

  const meta = LENS_META[lens];
  const id = params.get(meta.param);
  if (!id) return <Navigate to={`/?lview=${lens}`} replace />;
  const opt = meta.get(id);
  if (!opt) return <Navigate to={`/?lview=${lens}`} replace />;

  const exps = experiencesByTag(lens, id);
  const projs = lens === "role" ? [] : projectsByTag(lens, id);
  const hue = lens === "industry" ? "var(--hue)" : lensHueFor(lens, id);
  const FocusIcon = lensIconFor(lens, id);

  return (
    <div className="wrap section lens">
      <Link to={`/?lview=${lens}`} className="backbar">
        <CornerUpLeft size={15} /> All {meta.plural}
      </Link>

      <div className={`lens-focus-head ${lens === "industry" ? `hue-${id}` : ""}`}>
        <span
          className="lens-focus-ico"
          style={{
            color: hue,
            background: `color-mix(in oklab, ${hue} 13%, var(--card))`,
            borderColor: `color-mix(in oklab, ${hue} 28%, var(--rule))`,
          }}
        >
          {/* eslint-disable-next-line react-hooks/static-components -- lensIconFor returns a stable icon component from a static map */}
          <FocusIcon size={24} />
        </span>
        <div className="grow">
          <h1 className="display lens-focus-title">{opt.label}</h1>
          {opt.description && (
            <p className="section-sub" style={{ marginTop: 6 }}>
              {opt.description}
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ height: "40px", borderRadius: "25px" }}
          onClick={() => openAsk(`What has Srinath done in ${opt.label}?`)}
        >
          <Sparkles size={15} /> Ask AI
        </button>
      </div>

      <div className="lens-count-row mono">
        <span>
          <b>{exps.length}</b> {exps.length === 1 ? "role" : "roles"}
        </span>
        {projs.length > 0 && (
          <span>
            <b>{projs.length}</b> {projs.length === 1 ? "engagement" : "engagements"}
          </span>
        )}
      </div>

      {exps.length > 0 && (
        <section className="detail-section">
          <h2 className="eyebrow" style={{ marginBottom: 14 }}>
            Roles
          </h2>
          <div className="lens-exp-list">
            {exps.map((e) => (
              <ExpCard key={e.id} e={e} />
            ))}
          </div>
        </section>
      )}
      {projs.length > 0 && (
        <section className="detail-section">
          <h2 className="eyebrow" style={{ marginBottom: 14 }}>
            Engagements
          </h2>
          <div className="lens-proj-grid">
            {projs.map((p) => (
              <ProjCard key={p.id} p={p} />
            ))}
          </div>
        </section>
      )}
      <ShareThoughts className="page-share-bottom" />
    </div>
  );
}
